import { useState } from 'react';
import { Plus, Search, ViewGrid } from 'iconoir-react';
import { useTranslation } from 'react-i18next';
import { cn } from 'lib/utils';
import ConfigurationLayout from 'components/configuration/ConfigurationLayout';
import ConfigurationHeader from 'components/configuration/ConfigurationHeader';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from 'components/ui/select';
import {
  accountPrimaryButtonClassName,
  accountSectionClassName,
  accountSectionTitleClassName,
} from 'pages/account/components/account-styles';
import { CATEGORY_LABELS, INTEGRATION_APPS, type IntegrationCategory } from './integration-apps';
import { ConnectedAppRow } from './components/connected-app-row';
import { AvailableAppCard } from './components/available-app-card';

type StatusFilter = 'all' | 'connected' | 'available';
const ALL_CATEGORIES = 'all';

const connectedApps = INTEGRATION_APPS.filter((app) => app.connection);
const availableApps = INTEGRATION_APPS.filter((app) => !app.connection);

const STATUS_FILTERS: { value: StatusFilter; label: string; count: number }[] = [
  { value: 'all', label: 'All', count: INTEGRATION_APPS.length },
  { value: 'connected', label: 'Connected', count: connectedApps.length },
  { value: 'available', label: 'Available', count: availableApps.length },
];

/** Settings → Integrations (Figma 10631:26332). Static sample apps for now. */
export default function SettingsIntegrationsPage() {
  const { t } = useTranslation();
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState<StatusFilter>('all');
  const [category, setCategory] = useState<IntegrationCategory | typeof ALL_CATEGORIES>(
    ALL_CATEGORIES
  );

  const query = search.trim().toLowerCase();
  const matches = (app: (typeof INTEGRATION_APPS)[number]) =>
    (category === ALL_CATEGORIES || app.category === category) &&
    (!query || `${app.name} ${app.description}`.toLowerCase().includes(query));

  const visibleConnected = status === 'available' ? [] : connectedApps.filter(matches);
  const visibleAvailable = status === 'connected' ? [] : availableApps.filter(matches);
  const hasResults = visibleConnected.length > 0 || visibleAvailable.length > 0;

  return (
    <ConfigurationLayout className="gap-6 pb-8">
      <div className="flex w-full min-w-0 flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <ConfigurationHeader
          title={t('SETTINGS_INTEGRATIONS', { defaultValue: 'Integrations' })}
          subtitle={t('INTEGRATIONS_PAGE_SUBTITLE', {
            defaultValue:
              'Connect the apps your team uses, so Buddy and your agents can work with them.',
          })}
          icon={
            <ViewGrid
              width={20}
              height={20}
              strokeWidth={1.5}
              className="shrink-0 text-grey-900 dark:text-foreground"
            />
          }
        />
        <button
          type="button"
          className={cn(accountPrimaryButtonClassName, 'flex items-center justify-center gap-2')}
        >
          <Plus width={16} height={16} strokeWidth={1.5} />
          {t('INTEGRATIONS_REQUEST_APP', { defaultValue: 'Request an app' })}
        </button>
      </div>

      <div className="flex flex-col gap-3 lg:flex-row lg:items-center">
        <label className="flex h-10 w-full items-center gap-2 rounded-card border border-grey-100 bg-true-white px-3 lg:w-80 dark:border-border dark:bg-card">
          <Search
            width={16}
            height={16}
            strokeWidth={1.5}
            className="shrink-0 text-grey-500 dark:text-muted-foreground"
          />
          <input
            type="search"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder={t('INTEGRATIONS_SEARCH', { defaultValue: 'Search apps' })}
            aria-label={t('INTEGRATIONS_SEARCH', { defaultValue: 'Search apps' })}
            className="min-w-0 flex-1 bg-transparent text-sm text-grey-950 outline-none placeholder:text-grey-500 dark:text-foreground"
          />
        </label>

        <div role="group" className="flex flex-wrap gap-2">
          {STATUS_FILTERS.map((filter) => {
            const isActive = status === filter.value;
            return (
              <button
                key={filter.value}
                type="button"
                aria-pressed={isActive}
                onClick={() => setStatus(filter.value)}
                className={cn(
                  'flex h-8 items-center gap-1.5 rounded-full border px-3 text-sm',
                  isActive
                    ? 'border-grey-900 bg-grey-900 text-true-white dark:border-foreground dark:bg-foreground dark:text-background'
                    : 'border-grey-200 bg-true-white text-grey-900 hover:bg-grey-50 dark:border-border dark:bg-card dark:text-foreground'
                )}
              >
                {t(`INTEGRATIONS_FILTER_${filter.value.toUpperCase()}`, {
                  defaultValue: filter.label,
                })}
                <span className="text-xs opacity-70">{filter.count}</span>
              </button>
            );
          })}
        </div>

        <Select
          value={category}
          onValueChange={(value) =>
            setCategory(value as IntegrationCategory | typeof ALL_CATEGORIES)
          }
        >
          <SelectTrigger
            aria-label={t('INTEGRATIONS_CATEGORY', { defaultValue: 'Category' })}
            className="h-10 w-full rounded-xl border-grey-100 bg-true-white px-3 text-sm text-grey-950 shadow-none focus:ring-0 lg:ml-auto lg:w-52 dark:border-border dark:bg-card dark:text-foreground"
          >
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL_CATEGORIES}>
              {t('INTEGRATIONS_ALL_CATEGORIES', { defaultValue: 'All categories' })}
            </SelectItem>
            {Object.entries(CATEGORY_LABELS).map(([value, label]) => (
              <SelectItem key={value} value={value}>
                {label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {visibleConnected.length > 0 ? (
        <section className="flex flex-col gap-3">
          <h2 className={accountSectionTitleClassName}>
            {t('INTEGRATIONS_CONNECTED', { defaultValue: 'Connected' })}
          </h2>
          <ul
            className={cn(accountSectionClassName, 'divide-y divide-grey-100 dark:divide-border')}
          >
            {visibleConnected.map((app) => (
              <ConnectedAppRow key={app.id} app={app} />
            ))}
          </ul>
        </section>
      ) : null}

      {visibleAvailable.length > 0 ? (
        <section className="flex flex-col gap-3">
          <h2 className={accountSectionTitleClassName}>
            {t('INTEGRATIONS_AVAILABLE', { defaultValue: 'Available' })}
          </h2>
          <ul className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-3">
            {visibleAvailable.map((app) => (
              <AvailableAppCard key={app.id} app={app} />
            ))}
          </ul>
        </section>
      ) : null}

      {hasResults ? null : (
        <p className="py-6 text-center text-sm text-grey-500 dark:text-muted-foreground">
          {t('INTEGRATIONS_NO_RESULTS', { defaultValue: 'No apps match your search.' })}
        </p>
      )}
    </ConfigurationLayout>
  );
}
